import { Card } from "@/components/ui/card";
import { CheckCircle, Circle, HourglassMedium, XCircle } from "@phosphor-icons/react";

const LABELS = { facility: "Facility Representative", regional: "Regional Secretary", national: "National Manager" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "");

const stagesFor = (p) => {
  if (p.member_type === "intern") return ["facility", "regional"];
  if (p.self_registered) return ["facility", "regional", "national"];
  if (["national_manager", "super_admin"].includes(p.added_by_role)) return ["national"];
  return ["regional", "national"];
};

const Step = ({ state, title, detail, testId, last }) => {
  const Icon = { done: CheckCircle, current: HourglassMedium, rejected: XCircle, todo: Circle }[state];
  const color = { done: "text-emerald-600", current: "text-amber-600", rejected: "text-destructive", todo: "text-muted-foreground/50" }[state];
  return (
    <li className="relative flex gap-3 pb-5 last:pb-0" data-testid={testId} data-state={state}>
      {!last && <span className={`absolute left-[11px] top-7 bottom-0 w-0.5 ${state === "done" ? "bg-emerald-500" : "bg-border"}`} />}
      <Icon size={24} weight={state === "todo" ? "regular" : "fill"} className={`${color} shrink-0 relative`} />
      <div className="min-w-0">
        <p className={`text-sm ${state === "current" ? "font-semibold" : "font-medium"} ${state === "todo" ? "text-muted-foreground" : ""}`}>{title}</p>
        {detail && <p className={`text-xs mt-0.5 ${state === "rejected" ? "text-destructive" : "text-muted-foreground"}`}>{detail}</p>}
      </div>
    </li>
  );
};

export function ApplicationTracker({ profile: p }) {
  const status = p.application_status;
  if (!(status?.startsWith("pending_") || (status === "rejected" && p.submitted_at))) return null;
  const stages = stagesFor(p);
  const stopAt = status === "rejected" ? (p.rejected_stage || "").replace("pending_", "") : status.replace("pending_", "");
  const stopIdx = Math.max(0, stages.indexOf(stopAt));
  const steps = stages.map((s, i) => {
    if (i < stopIdx) return { state: "done", title: `${LABELS[s]} approved`, detail: fmt(p[`${s}_approved_at`]) && `on ${fmt(p[`${s}_approved_at`])}` };
    if (i === stopIdx && status === "rejected") return { state: "rejected", title: `Returned by ${LABELS[s]}`, detail: `${fmt(p.rejected_at) ? `on ${fmt(p.rejected_at)}. ` : ""}Reason: ${p.rejection_reason || "not given"}. Update your details and resubmit.` };
    if (i === stopIdx) return { state: "current", title: `Waiting for ${LABELS[s]} review`, detail: p.days_waiting != null ? `Waiting ${p.days_waiting} day${p.days_waiting === 1 ? "" : "s"}` : "" };
    return { state: "todo", title: `${LABELS[s]} review`, detail: i === stopIdx + 1 && status !== "rejected" ? "Next step" : "" };
  });
  const prefix = p.member_type === "intern" ? "INT-" : p.member_type === "private" ? "P-" : "G";
  const all = [
    { state: "done", title: "Application submitted", detail: p.submitted_at ? `on ${fmt(p.submitted_at)}` : "" },
    ...steps,
    { state: "todo", title: "Membership number issued", detail: `Your number will begin ${prefix}` },
  ];
  return (
    <Card className="mt-4 p-5 border-border" data-testid="application-tracker">
      <p className="text-sm font-semibold mb-4">Application progress</p>
      <ol>
        {all.map((s, i) => <Step key={i} {...s} last={i === all.length - 1} testId={`tracker-step-${i}`} />)}
      </ol>
    </Card>
  );
}
